import { useState, useEffect, useMemo } from 'react';
import {
  Modal, ModalDialog, ModalClose, Typography, Box, CircularProgress, List,
  ListItemButton, ListItemContent, Input, IconButton, Alert, Button, Avatar, Chip, useTheme,
} from '@mui/joy';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';

import { APIGetSpecializations, APIGetDoctors } from '../../api';

/**
 * Переиспользуемый выбор специалиста: специализация -> список врачей.
 * Пропсы:
 *  - open, onClose
 *  - token
 *  - onSelect(doctor)  — вызывается при клике на врача
 *  - submitting        — блокирует список во время отправки
 *  - title             — заголовок модалки
 */
export default function ModalSelectDoctor({ open, onClose, token, onSelect, submitting = false, title = 'Выберите специалиста' }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [step, setStep] = useState('type'); // 'type' | 'list'
  const [specs, setSpecs] = useState([]);
  const [specsLoading, setSpecsLoading] = useState(false);
  const [selectedSpec, setSelectedSpec] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState(false);
  const [specFallback, setSpecFallback] = useState(false); // бэкенд вернул spec_filter_empty → показаны все врачи
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    setStep('type'); setSelectedSpec(null); setDoctors([]); setSearch('');
    setSpecsLoading(true);
    APIGetSpecializations(token)
      .then((data) => setSpecs(data?.specializations || []))
      .catch(() => setSpecs([]))
      .finally(() => setSpecsLoading(false));
  }, [open, token]);

  // spec === null → «Все специалисты» (без фильтра). Иначе фильтр по специализации.
  const selectSpec = async (spec) => {
    setSelectedSpec(spec); setStep('list'); setSearch(''); setDocError(false); setSpecFallback(false); setDocLoading(true);
    try {
      const params = spec?.slug ? { specialization: spec.slug } : {};
      const data = await APIGetDoctors(token, params);
      setDoctors(data?.doctors || []);
      // Бэкенд: если по специализации никого нет — вернёт всех врачей и spec_filter_empty=true.
      setSpecFallback(!!data?.spec_filter_empty);
    } catch (e) {
      setDocError(true);
    } finally {
      setDocLoading(false);
    }
  };

  const backToTypes = () => { setStep('type'); setDoctors([]); setSearch(''); setDocError(false); };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return doctors;
    return doctors.filter((d) => (d.name || '').toLowerCase().includes(q));
  }, [doctors, search]);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog layout={isMobile ? 'fullscreen' : 'center'} sx={isMobile ? {} : { maxWidth: 520, width: '100%' }}>
        <ModalClose />
        {step === 'type' ? (
          <>
            <Typography level="title-lg" sx={{ mb: 1 }}>{title}</Typography>
            <Typography level="body-sm" textColor="text.tertiary" sx={{ mb: 1 }}>Сначала выберите тип специалиста</Typography>
            {specsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
            ) : (
              <List sx={{ gap: 0.5 }}>
                <ListItemButton onClick={() => selectSpec(null)} sx={{ borderRadius: 'sm' }}>
                  <ListItemContent><Typography level="title-sm">Все специалисты</Typography></ListItemContent>
                </ListItemButton>
                {specs.length === 0 && (
                  <Typography level="body-sm" textColor="text.tertiary" sx={{ px: 2, pt: 1 }}>
                    Отдельные специализации не заданы — нажмите «Все специалисты», чтобы увидеть весь список врачей.
                  </Typography>
                )}
                {specs.map((s) => (
                  <ListItemButton key={s.id} onClick={() => selectSpec(s)} sx={{ borderRadius: 'sm' }}>
                    <ListItemContent><Typography level="title-sm">{s.name}</Typography></ListItemContent>
                  </ListItemButton>
                ))}
              </List>
            )}
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <IconButton size="sm" variant="plain" onClick={backToTypes}><ArrowBackRoundedIcon /></IconButton>
              <Typography level="title-lg">{selectedSpec?.name || 'Все специалисты'}</Typography>
            </Box>
            <Input size="sm" placeholder="Поиск по имени" value={search}
              onChange={(e) => setSearch(e.target.value)} startDecorator={<SearchRoundedIcon />} sx={{ mb: 1 }} />
            {specFallback && (
              <Alert color="neutral" variant="soft" sx={{ mb: 1 }}>
                По выбранной специализации врачей нет — показаны все доступные специалисты.
              </Alert>
            )}
            {docLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
            ) : docError ? (
              <Alert color="danger" variant="soft" endDecorator={
                <Button size="sm" variant="soft" color="danger" onClick={() => selectSpec(selectedSpec)}>Повторить</Button>
              }>Не удалось загрузить список врачей</Alert>
            ) : (
              <List sx={{ maxHeight: '60vh', overflow: 'auto', gap: 0.5 }}>
                {filtered.length === 0 && (
                  <Typography level="body-sm" textColor="text.tertiary" sx={{ p: 2 }}>Список специалистов пуст. Обратитесь к администратору.</Typography>
                )}
                {filtered.map((d) => (
                  <ListItemButton key={d.id} disabled={submitting} onClick={() => onSelect(d)} sx={{ borderRadius: 'sm' }}>
                    <Avatar size="sm" src={d.avatar_url || undefined} sx={{ mr: 1.5 }}>{(d.name || '?').slice(0, 1)}</Avatar>
                    <ListItemContent>
                      <Typography level="title-sm">{d.name}</Typography>
                      {Array.isArray(d.specialization) && d.specialization.length > 0 && (
                        <Typography level="body-xs" textColor="text.tertiary">
                          {d.specialization.map((s) => s.name || s).join(', ')}
                        </Typography>
                      )}
                      {d.avg_rating != null && d.reviews_count > 0 && (
                        <Chip size="sm" variant="soft" color="warning"
                          startDecorator={<StarRoundedIcon sx={{ fontSize: 14 }} />} sx={{ mt: 0.25 }}>
                          {Number(d.avg_rating).toFixed(1)} · {d.reviews_count}
                        </Chip>
                      )}
                    </ListItemContent>
                  </ListItemButton>
                ))}
              </List>
            )}
          </>
        )}
      </ModalDialog>
    </Modal>
  );
}